<template>
  <div class="search-field" :class="`search-field--${searchVariant}`">
    <label class="sr-only" for="search-input">Search</label>
    <div
      class="search-field__combobox"
      :class="{
        'is-focused': isFocused
      }"
      role="combobox"
      :aria-expanded="isOpen ? 'true' : 'false'"
    >
      <div class="u-flex middle-xs search-field__field">
        <input
          id="search-input"
          v-model="searchText"
          class="search-field__input u-flex-1 u-padding-left-4x"
          type="text"
          autocomplete="off"
          aria-autocomplete="both"
          aria-haspopup="false"
          data-cy="search-field-input"
          :placeholder="placeholder"
          :autofocus="autofocus"
          @input="handleInput"
          @keyup.down="handleArrowDown"
          @keyup.up="handleArrowUp"
          @keyup.enter="handleEnter"
          @focus="isFocused = true"
          @blur="isFocused = false"
          @keyup.esc="isOpen = false"
        />
        <button
          v-if="showClearIcon"
          aria-label="Clear search"
          data-cy="search-field-clear"
          class="search-field__clear unstyled-button"
          type="button"
          @click="clearSearch"
        >
          <SvgIcon name="cross" />
        </button>
        <BaseButton
          type="submit"
          variant="primary"
          class="search-field__submit u-margin-left-4x"
          aria-label="Submit search"
          icon="magnifying-glass"
          size="sm"
          data-cy="search-field-submit"
          :leading-icon="searchVariant === SEARCH_VARIANTS.DESCRIPTIVE"
          @click.native="closeList"
        >
          <template v-if="searchVariant === SEARCH_VARIANTS.DESCRIPTIVE">{{
            $t('common.btnSearch')
          }}</template>
        </BaseButton>
      </div>
      <div
        v-if="isOpen"
        class="search-field__results"
        data-cy="search-field-results"
        role="listbox"
      >
        <ul class="search-field__list">
          <li
            v-for="(result, i) in items"
            :key="`search-field-results-${i}`"
            class="search-field__result"
            data-cy="search-field-item"
            :class="{ 'is-active': i === arrowCounter }"
            role="option"
            @click="setResult(result)"
          >
            <div class="search-field__item" v-html="highlightResult(result)" />
          </li>
        </ul>
      </div>

      <GlobalEvents target="document" @click="handleClickOutside" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { SvgIcon, BaseButton } from '@te/cdev-design-system';

enum SEARCH_VARIANTS {
  DESCRIPTIVE = 'descriptive',
  ICON_ONLY = 'icon-only'
}
export default defineComponent({
  name: 'SearchField',

  components: {
    SvgIcon,
    BaseButton
  },

  props: {
    items: {
      type: Array as PropType<Record<string, string>[]>,
      required: false,
      default: () => [] as Record<string, string>[]
    },

    property: {
      type: String,
      default: 'name'
    },

    placeholder: {
      type: String,
      default: ''
    },

    variant: {
      type: String,
      default: SEARCH_VARIANTS.DESCRIPTIVE,
      validator: (value: SEARCH_VARIANTS): boolean => {
        return [
          SEARCH_VARIANTS.DESCRIPTIVE,
          SEARCH_VARIANTS.ICON_ONLY
        ].includes(value);
      }
    },

    trim: {
      type: Boolean,
      default: false
    },

    value: {
      type: String,
      default: ''
    },

    autofocus: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isOpen: false,
      searchText: '',
      arrowCounter: -1,
      isFocused: false,
      isMobile: window.innerWidth < 767,
      SEARCH_VARIANTS
    };
  },

  computed: {
    hasResults(): boolean {
      return !!this.items.length;
    },
    showClearIcon(): boolean {
      return !!this.searchText;
    },
    searchVariant(): string {
      return this.isMobile ? SEARCH_VARIANTS.ICON_ONLY : this.variant;
    },
    searchTextValue(): string {
      let value = this.searchText;
      if (this.trim) {
        value = value.trim();
      }
      return value;
    }
  },

  watch: {
    items() {
      this.isOpen = this.hasResults;
    },
    value: {
      immediate: true,
      handler() {
        this.searchText = this.value;
      }
    }
  },

  methods: {
    clearSearch() {
      this.searchText = '';
      this.$emit('input', this.searchTextValue);
      this.$emit('input-cleared');
      document.getElementById('search-input').focus();
    },
    handleInput() {
      this.$emit('input', this.searchTextValue);
      this.isOpen = this.hasResults;
    },
    setResult(result: Record<string, string>) {
      this.$emit('select', result);
      this.searchText = result[this.property];
      this.isOpen = false;
    },

    handleArrowDown() {
      if (this.arrowCounter < this.items.length - 1) {
        this.arrowCounter = this.arrowCounter + 1;
      }
    },

    handleArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter = this.arrowCounter - 1;
      }
    },

    handleEnter() {
      if (this.arrowCounter > -1 && this.arrowCounter < this.items.length) {
        const result: Record<string, string> = this.items[this.arrowCounter];
        this.$emit('select', result);
        this.searchText = result[this.property];
      }

      this.isOpen = false;
      this.arrowCounter = -1;
    },

    handleClickOutside(evt: Event) {
      const element = evt.target;

      if (element instanceof Node && !this.$el.contains(element)) {
        this.isOpen = false;
        this.arrowCounter = -1;
      }
    },

    highlightResult(result: Record<string, string>) {
      if (!this.searchTextValue) {
        return result[this.property];
      }
      const regExp = new RegExp(this.searchTextValue, 'gi');
      return result[this.property].replace(regExp, (match) => {
        return `<b>${match}</b>`;
      });
    },

    closeList() {
      this.isOpen = false;
    }
  }
});
</script>

<style lang="scss">
.search-field {
  // For descriptive variant (default)
  max-width: 600px;
  min-width: 250px;

  @include breakpoint('XS') {
    min-width: 400px;

    &--icon-only {
      min-width: 300px;
    }
  }

  &__combobox {
    text-align: left;
    z-index: 10;
    position: relative;

    &:hover {
      .search-field__field {
        box-shadow: get-form-selecting-box-shadow();
      }
    }

    &.is-focused {
      .search-field__field {
        @include generate-form-elements-focus();
      }
    }
  }

  &__field {
    background: color('base-background');
    border-radius: rounding('full');
    border: 1px solid color(form-default-input-lines);
    padding: spacing('1x');
    transition: box-shadow 200ms cubic-bezier(0, 0.55, 0.45, 1);
  }

  &__submit {
    border: none;
    border-radius: rounding('full');

    &:hover {
      box-shadow: none !important;
      transform: none;
    }
  }

  &__input {
    border-radius: rounding('6x');
    border: none;
    min-width: 100px;

    &::-ms-clear {
      display: none;
      width: 0;
      height: 0;
    }

    &:focus {
      box-shadow: none;
      outline: none;
    }
  }

  &__clear {
    line-height: 0;
  }

  &__results {
    max-height: 600px;
    overflow: auto;
    width: 100%;
    position: absolute;
    background: color(base-background);
    border: 1px solid color(lines);
    border-radius: rounding('6x');
    margin-top: spacing('4x');
    padding: spacing('4x') spacing('3x');
    box-shadow: elevation('md');
  }

  &__list {
    padding: 0;
    margin: 0;
  }

  &__result {
    list-style: none;
    cursor: pointer;
    border-radius: 8px;
    margin-bottom: spacing('1x');

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background-color: color(form-hover-input-background);
    }

    &.is-active {
      background-color: color(selecting-background);
    }
  }

  &__item {
    padding: spacing('2x') spacing('3x');
  }
}
</style>
